import { useLazyQuery } from '@apollo/client';
import React, { useCallback, useState } from 'react';
import { z } from 'zod';

import { useApiClient } from '@eluve/api-client-provider';
import { SearchCombobox } from '@eluve/blocks';
import {
  Button,
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Heading,
  Icon,
  Separator,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { FeatureFlagKeys, featureFlagsWithPayloads } from '@eluve/posthog';

import { TenantUserFragment } from '../tenant/$tenantId/users/operations';

const uuidSchema = z.string().uuid();

const searchTenantsByNameQuery = graphql(`
  query SearchTenantsByNameForFeatureFlagTester($search: String!) {
    tenants(
      orderBy: { name: ASC }
      where: {
        _and: [{ deletedAt: { _isNull: true } }, { name: { _ilike: $search } }]
      }
      limit: 50
    ) {
      __typename
      id
      name
    }
  }
`);

const searchTenantsByIdQuery = graphql(`
  query SearchTenantsByIdForFeatureFlagTester($id: uuid!) {
    tenants(
      orderBy: { name: ASC }
      where: { _and: [{ deletedAt: { _isNull: true } }, { id: { _eq: $id } }] }
      limit: 50
    ) {
      __typename
      id
      name
    }
  }
`);

const searchTenantUsersByNameQuery = graphql(
  `
    query SearchTenantUsersByNameForFeatureFlagTester(
      $tenantId: uuid!
      $emailSearch: citext!
      $nameSearch: String!
    ) {
      tenantUsers(
        where: {
          _and: [
            { tenantId: { _eq: $tenantId } }
            {
              _or: [
                { user: { email: { _ilike: $emailSearch } } }
                { user: { firstName: { _ilike: $nameSearch } } }
                { user: { lastName: { _ilike: $nameSearch } } }
              ]
            }
          ]
        }
        limit: 50
      ) {
        ...TenantUser
      }
    }
  `,
  [TenantUserFragment],
);

const searchTenantUsersByIdQuery = graphql(
  `
    query SearchTenantUsersByIdForFeatureFlagTester(
      $tenantId: uuid!
      $userId: uuid!
    ) {
      tenantUsers(
        where: {
          _and: [{ tenantId: { _eq: $tenantId } }, { userId: { _eq: $userId } }]
        }
        limit: 50
      ) {
        ...TenantUser
      }
    }
  `,
  [TenantUserFragment],
);

interface TestResult {
  flagName: string;
  isEnabled: boolean;
  payload?: unknown;
  parsedSuccessfully: boolean;
  tenantName: string;
  userEmail: string;
}

interface SelectedTenant {
  id: string;
  name: string;
}

interface SelectedUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

export const FeatureFlagTesterDialog: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<SelectedTenant | null>(
    null,
  );
  const [selectedUser, setSelectedUser] = useState<SelectedUser | null>(null);
  const [selectedFlagName, setSelectedFlagName] =
    useState<FeatureFlagKeys | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const apiClient = useApiClient();

  const [searchTenantsByName] = useLazyQuery(searchTenantsByNameQuery);
  const [searchTenantsById] = useLazyQuery(searchTenantsByIdQuery);
  const [searchUsersByName] = useLazyQuery(searchTenantUsersByNameQuery);
  const [searchUsersById] = useLazyQuery(searchTenantUsersByIdQuery);

  const toast = useToast();

  const featureFlagKeys = Object.keys(
    featureFlagsWithPayloads,
  ) as FeatureFlagKeys[];

  const searchTenants = useCallback(
    async (searchTerm: string): Promise<SelectedTenant[]> => {
      const isUuid = uuidSchema.safeParse(searchTerm).success;
      if (isUuid) {
        const result = await searchTenantsById({
          variables: { id: searchTerm },
        });
        return (
          result.data?.tenants.map((t) => ({ id: t.id, name: t.name })) ?? []
        );
      } else {
        const result = await searchTenantsByName({
          variables: { search: `%${searchTerm}%` },
        });
        return (
          result.data?.tenants.map((t) => ({ id: t.id, name: t.name })) ?? []
        );
      }
    },
    [searchTenantsById, searchTenantsByName],
  );

  const searchUsers = useCallback(
    async (searchTerm: string): Promise<SelectedUser[]> => {
      if (!selectedTenant) return [];

      const isUuid = uuidSchema.safeParse(searchTerm).success;
      if (isUuid) {
        const result = await searchUsersById({
          variables: {
            tenantId: selectedTenant.id,
            userId: searchTerm,
          },
        });
        return (
          result.data?.tenantUsers
            .map((tu) => {
              const user = tu.user;
              if (!user) return null;
              return {
                id: user.id,
                email: user.email,
                firstName: user.firstName ?? undefined,
                lastName: user.lastName ?? undefined,
              } as SelectedUser;
            })
            .filter((u): u is SelectedUser => u !== null) ?? []
        );
      } else {
        const result = await searchUsersByName({
          variables: {
            tenantId: selectedTenant.id,
            emailSearch: `%${searchTerm}%`,
            nameSearch: `%${searchTerm}%`,
          },
        });
        return (
          result.data?.tenantUsers
            .map((tu) => {
              const user = tu.user;
              if (!user) return null;
              return {
                id: user.id,
                email: user.email,
                firstName: user.firstName ?? undefined,
                lastName: user.lastName ?? undefined,
              } as SelectedUser;
            })
            .filter((u): u is SelectedUser => u !== null) ?? []
        );
      }
    },
    [selectedTenant, searchUsersById, searchUsersByName],
  );

  const handleTest = useCallback(async () => {
    if (!selectedTenant || !selectedUser || !selectedFlagName) {
      toast.error('Please select a tenant, user, and feature flag');
      return;
    }

    setIsLoading(true);
    setTestResult(null);
    const response = await apiClient.adminFeatureFlags.testPosthogFeatureFlag({
      body: {
        tenantId: selectedTenant.id,
        userId: selectedUser.id,
        flagName: selectedFlagName,
      },
    });

    if (response.status === 200) {
      setTestResult(response.body);
    } else {
      toast.error('Failed to test feature flag');
    }

    setIsLoading(false);
  }, [selectedTenant, selectedUser, selectedFlagName, apiClient, toast]);

  const handleReset = useCallback(() => {
    setSelectedTenant(null);
    setSelectedUser(null);
    setSelectedFlagName(null);
    setTestResult(null);
  }, []);

  const handleDialogOpen = useCallback(
    (open: boolean) => {
      setOpen(open);
      if (!open) {
        handleReset();
      }
    },
    [handleReset],
  );

  const handleTenantChange = useCallback((tenant: SelectedTenant | null) => {
    setSelectedTenant(tenant);
    setSelectedUser(null);
    setTestResult(null);
  }, []);

  const handleUserChange = useCallback((user: SelectedUser | null) => {
    setSelectedUser(user);
    setTestResult(null);
  }, []);

  const handleFlagChange = useCallback((flag: FeatureFlagKeys | null) => {
    setSelectedFlagName(flag);
    setTestResult(null);
  }, []);

  return (
    <Dialog open={open} onOpenChange={handleDialogOpen}>
      <DialogTrigger asChild>
        <Button>
          <Icon name="FlaskConical" size="auto" />
          Test PostHog Flags
        </Button>
      </DialogTrigger>
      <DialogContent
        size="3xl"
        className="max-h-[90vh] min-h-[80vh] overflow-y-auto"
      >
        <Stack gap={2}>
          <Stack className="grow" gap={2} fullHeight>
            <Stack gap={3} direction="horizontal">
              <Icon name="FlaskConical" size="3xl" />
              <DialogHeader>
                <DialogTitle>
                  <Heading size="2xl" color="secondary">
                    Test PostHog Feature Flags
                  </Heading>
                </DialogTitle>
              </DialogHeader>
            </Stack>

            <Separator className="my-2" />

            <Text>
              Search for a tenant and user, then select a feature flag to test
              the PostHog feature flag payload response.
            </Text>

            <Stack gap={4}>
              <Stack gap={2}>
                <Text size="xs" weight="semibold">
                  1. Select Tenant
                </Text>
                <SearchCombobox
                  value={selectedTenant}
                  onValueChange={handleTenantChange}
                  fetchItems={searchTenants}
                  renderItem={(tenant) => (
                    <Stack>
                      <Text size="xs">{tenant.name}</Text>
                      <Text size="xs" color="tertiary">
                        {tenant.id}
                      </Text>
                    </Stack>
                  )}
                  renderSelected={(tenant) => (
                    <Stack>
                      <Text size="xs">{tenant.name}</Text>
                      <Text size="xs" color="tertiary">
                        {tenant.id}
                      </Text>
                    </Stack>
                  )}
                  getSearchValue={(tenant) => `${tenant.name} ${tenant.id}`}
                  getKey={(tenant) => tenant.id}
                  placeholder="Select tenant..."
                  searchPlaceholder="Search by tenant name or ID..."
                  emptyMessage="No tenants found"
                  className="w-full"
                />
              </Stack>

              <Stack gap={2}>
                <Text size="xs" weight="semibold">
                  2. Select User
                </Text>
                <SearchCombobox
                  value={selectedUser}
                  onValueChange={handleUserChange}
                  fetchItems={searchUsers}
                  renderItem={(user) => {
                    const displayName =
                      `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
                      user.email;
                    return (
                      <Stack>
                        <Text size="xs">{displayName}</Text>
                        <Text size="xs" color="tertiary">
                          {user.email}
                        </Text>
                      </Stack>
                    );
                  }}
                  renderSelected={(user) => {
                    const displayName =
                      `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
                      user.email;
                    return (
                      <Stack>
                        <Text size="xs">{displayName}</Text>
                        <Text size="xs" color="tertiary">
                          {user.email}
                        </Text>
                      </Stack>
                    );
                  }}
                  getSearchValue={(user) => {
                    const displayName =
                      `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
                      user.email;
                    return `${displayName} ${user.email} ${user.id}`;
                  }}
                  getKey={(user) => user.id}
                  placeholder="Select user..."
                  searchPlaceholder="Search by email, name, or ID..."
                  emptyMessage="No users found"
                  className="w-full"
                  disabled={!selectedTenant}
                />
              </Stack>

              <Stack gap={2}>
                <Text size="xs" weight="semibold">
                  3. Select Feature Flag
                </Text>
                <Combobox disabled={!selectedUser}>
                  <ComboboxSelectButton className="w-full">
                    {selectedFlagName ?? 'Select feature flag...'}
                  </ComboboxSelectButton>
                  <ComboboxDropdown searchPlaceholder="Search feature flags...">
                    {featureFlagKeys.map((flag) => (
                      <ComboboxOption
                        key={flag}
                        onSelect={() => handleFlagChange(flag)}
                        searchValue={flag}
                      >
                        <Text size="xs">{flag}</Text>
                      </ComboboxOption>
                    ))}
                  </ComboboxDropdown>
                </Combobox>
              </Stack>

              {/* Results Display */}
              {testResult && (
                <>
                  <Separator className="my-2" />
                  <Stack gap={2}>
                    <Text size="xs" weight="semibold">
                      Results
                    </Text>

                    <Stack gap={4} direction="horizontal">
                      <Stack>
                        <Text inline size="xs" color="tertiary">
                          Tenant
                        </Text>
                        <Text inline size="xs">
                          {testResult.tenantName}
                        </Text>
                      </Stack>
                      <Stack>
                        <Text inline size="xs" color="tertiary">
                          User
                        </Text>
                        <Text inline size="xs">
                          {testResult.userEmail}
                        </Text>
                      </Stack>
                    </Stack>

                    <Stack gap={4} direction="horizontal">
                      <Stack>
                        <Text inline size="xs" color="tertiary">
                          Flag Enabled
                        </Text>
                        <Text
                          inline
                          size="xs"
                          color={testResult.isEnabled ? 'primary' : 'tertiary'}
                        >
                          {testResult.isEnabled ? 'Yes' : 'No'}
                        </Text>
                      </Stack>
                      <Stack>
                        <Text inline size="xs" color="tertiary">
                          Schema Validation
                        </Text>
                        <Text
                          inline
                          size="xs"
                          color={
                            testResult.parsedSuccessfully
                              ? 'primary'
                              : 'tertiary'
                          }
                        >
                          {testResult.parsedSuccessfully ? 'Passed' : 'Failed'}
                        </Text>
                      </Stack>
                    </Stack>

                    <Stack gap={1}>
                      <Text size="xs" weight="semibold">
                        Payload
                      </Text>
                      <div className="max-h-96 overflow-auto rounded-md bg-bg-secondary p-3">
                        <pre className="text-xs">
                          {JSON.stringify(testResult.payload ?? null, null, 2)}
                        </pre>
                      </div>
                    </Stack>
                  </Stack>
                </>
              )}
            </Stack>
          </Stack>

          <DialogFooter className="w-full">
            <Button variant="outline" onClick={handleReset}>
              Reset
            </Button>
            <Button
              onClick={handleTest}
              disabled={
                isLoading ||
                !selectedTenant ||
                !selectedUser ||
                !selectedFlagName
              }
            >
              {isLoading ? 'Testing...' : 'Test Flag'}
            </Button>
          </DialogFooter>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
